import React, { useEffect, useState } from "react";
import { Box, Tab } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { get } from "../../../redux/ApiHelper";
import { GcTransactionInterface } from "../../../interfaces/guruCoin.interface";
import { Column } from "../../../interfaces/table.interface";
import TableComponent from "../../../components/table/TableComponent";
import { formatDateTime } from "../../../utils/date&time.util";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { DispatchType } from "../../../interfaces/redux.interface";
import { startLoading, stopLoading } from "../../../redux/slices/WindowSlice";
import { getInfo } from "../../../redux/slices/InfoSlice";
import { gcToInr } from "../../../utils/helper.util";
import TrialClassPaymentsTab from "./TrialClassPaymentsTab";
import OtherCoinTransferTab from "./OtherCoinTransferTab";

type GcHistoryTab = "guru-coin" | "trial-payments" | "other";

interface GuruCoinTransactionProps {
  initialTab?: GcHistoryTab;
}

const GuruCoinTransaction = ({
  initialTab = "guru-coin",
}: GuruCoinTransactionProps) => {
  const [activeTab, setActiveTab] = useState<GcHistoryTab>(initialTab);
  const [gcTransactionList, setGcTransactionList] = useState<
    GcTransactionInterface[]
  >([]);
  const [filteredGcTransactionList, setFilteredGcTransactionList] = useState<
    GcTransactionInterface[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { signedAdmin } = useSelector((state: RootState) => state.admin);
  const { info } = useSelector((state: RootState) => state.info);
  const dispatch: DispatchType = useDispatch();

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set("type", String(signedAdmin.type));
    queryParams.set("adminId", String(signedAdmin.id));
    get(`/admin/gc-transaction/all?${queryParams.toString()}`).then((res) => {
      const list = Array.isArray(res) ? res : [];
      setGcTransactionList(list);
      setFilteredGcTransactionList(list);
    });
  }, [signedAdmin.id, signedAdmin.type]);

  useEffect(() => {
    if (!info) {
      dispatch(startLoading());
      dispatch(getInfo()).finally(() => {
        dispatch(stopLoading());
      });
    }
    //eslint-disable-next-line
  }, [info]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    const lowerQuery = query.toLowerCase();

    const filteredList = gcTransactionList?.filter((transaction) => {
      const parentName = transaction.parentId?.name?.toLowerCase() || "";
      const teacherName = transaction.teacherId?.name?.toLowerCase() || "";
      const studentName = transaction.studentId?.name?.toLowerCase() || "";
      const classNumber =
        transaction?.classId?.currentTuitionClassNo?.toString() || "";

      return (
        transaction?._id?.toLowerCase().includes(lowerQuery) ||
        transaction?.coins?.toString().includes(lowerQuery) ||
        transaction.teacherValue?.toString().includes(lowerQuery) ||
        parentName.includes(lowerQuery) ||
        teacherName.includes(lowerQuery) ||
        studentName.includes(lowerQuery) ||
        classNumber.includes(lowerQuery)
      );
    });

    setFilteredGcTransactionList(filteredList);
  };

  const columns: Column[] = [
    { select: "_id", header: "Transaction ID", align: "left" },
    {
      select: "coins",
      header: "Coins",
    },
    {
      header: "Inr Value",
      render: (_value: unknown, row: GcTransactionInterface) =>
        gcToInr(row.coins, info?.conversionRate),
    },
    {
      header: "Date & Time",
      render: (_value: unknown, row: GcTransactionInterface) =>
        formatDateTime(row?.createdAt),
    },
    {
      header: "Parent Name",
      render: (_value: unknown, row: GcTransactionInterface) =>
        row?.parentId?.name || "N/A",
    },
    {
      header: "Teacher Name",
      render: (_value: unknown, row: GcTransactionInterface) =>
        row?.teacherId?.name || "N/A",
    },
    {
      header: "Student Name",
      render: (_value: unknown, row: GcTransactionInterface) =>
        row?.studentId?.name || "N/A",
    },
    {
      header: "Class ID",
      render: (_value: unknown, row: GcTransactionInterface) =>
        row?.classId?._id || "N/A",
    },
  ];

  return (
    <div className="gcTransaction table-container">
      <div className="title">Guru Coin Transactions</div>

      <TabContext value={activeTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <TabList
            onChange={(_e, value: GcHistoryTab) => setActiveTab(value)}
            aria-label="Guru coin transaction categories"
            sx={{
              "& .MuiTab-root": {
                color: "var(--color-light-gray)",
                textTransform: "none",
                "&:hover": {
                  color: "var(--color-primary)!important",
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px 8px 0 0",
                },
              },
              "& .Mui-selected": {
                color: "var(--color-primary)!important",
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                fontWeight: "bold",
                borderRadius: "8px 8px 0 0",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "var(--color-primary)",
              },
            }}
          >
            <Tab label="Guru Coin" value="guru-coin" />
            <Tab label="Trial Class Payments" value="trial-payments" />
            <Tab label="Other Coin Transfer" value="other" />
          </TabList>
        </Box>

        <TabPanel value="guru-coin" sx={{ px: 0 }}>
          <div className="search-container mb-4">
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700"
              placeholder="Search transactions"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
          <TableComponent
            columns={columns}
            data={filteredGcTransactionList}
          />
        </TabPanel>

        <TabPanel value="trial-payments" sx={{ px: 0 }}>
          <TrialClassPaymentsTab />
        </TabPanel>

        <TabPanel value="other" sx={{ px: 0 }}>
          <OtherCoinTransferTab />
        </TabPanel>
      </TabContext>
    </div>
  );
};

export default GuruCoinTransaction;
